import { Snackbar } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SurveyService } from "../api/SurveyService";
import { Button } from "../components/ui/Button/Button";
import { LoadingIndicator } from "../components/ui/LoadingIndicator/LoadingIndicator";
import { Paper } from "../components/ui/Paper/Paper";
import { Typography } from "../components/ui/Typography/Typography";
import { useSurvey } from "../hooks/useSurvey";
import useTranslation from "../hooks/useTranslation";
import SurveyDispatchManager from "../models/surveyDispatchManager";
import { GetSurveyDispatchesResponse, Survey } from "../types/survey";
import { ProtectedRoutes } from "../utils/routes.utils";
import "./SurveySelectionPage.scss";

export const SurveySelectionPage = () => {
  const { t } = useTranslation("surveySelectionPage");
  const { setCurrentSurvey } = useSurvey();
  const navigate = useNavigate();
  const surveyService = new SurveyService();
  const [dispatchManager, setDispatchManager] =
    useState<SurveyDispatchManager>();

  const launchSurveyMutation = useMutation({
    mutationFn: (surveyId: string) => surveyService.createDispatch(surveyId),
  });

  // Fetch available surveys
  const {
    data: surveys,
    isLoading: surveysLoading,
    error: surveysError,
  } = useQuery<Survey[], AxiosError>(["surveys"], () =>
    surveyService.getSurveys()
  );

  const {
    data: surveyDispatchesData,
    isLoading: surveyDispatchesLoading,
    error: surveyDispatchesError,
    refetch: surveyDispatchesRefetch,
  } = useQuery<GetSurveyDispatchesResponse[], AxiosError>(
    ["surveyDispatches"],
    () => surveyService.getDispatches()
  );

  useEffect(() => {
    if (surveyDispatchesData) {
      setDispatchManager(new SurveyDispatchManager(surveyDispatchesData, t));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyDispatchesData]);

  // If only one survey is available, auto-select it
  useEffect(() => {
    if (
      surveys &&
      surveys.length === 1 &&
      dispatchManager &&
      dispatchManager.canLaunchNewDispatch()
    ) {
      handleSelectSurvey(surveys[0]);
    }

    if (dispatchManager && !dispatchManager.canLaunchNewDispatch()) {
      const latestId = dispatchManager.getLatestId();

      if (latestId) {
        navigate(`${ProtectedRoutes.LAUNCH}/${latestId}`);
      } else {
        navigate(ProtectedRoutes.WORKPLACE_CULTURE);
      }
    }
  }, [surveys, dispatchManager]);

  // Check for errors first, before checking loading/dispatchManager state
  if (surveysError || surveyDispatchesError) {
    return (
      <div className="SurveySelectionPage" data-testid="survey-selection-error">
        <Typography desktop="h2" color="error">
          {t("surveySelection.error")}
        </Typography>
        <Typography desktop="body1">{t("surveySelection.errorMsg")}</Typography>
      </div>
    );
  }

  if (
    surveysLoading ||
    surveyDispatchesLoading ||
    !dispatchManager ||
    !dispatchManager.canLaunchNewDispatch()
  ) {
    return (
      <div
        className="SurveySelectionPage"
        data-testid="survey-selection-loading"
      >
        <LoadingIndicator />
      </div>
    );
  }

  const handleSelectSurvey = (survey: Survey) => {
    setCurrentSurvey(survey);
    launchSurveyMutation.mutateAsync(survey.id, {
      onSuccess: (data) => {
        navigate(`${ProtectedRoutes.LAUNCH}/${data.survey_dispatch.id}`);
      },
    });

    navigate(ProtectedRoutes.LAUNCH);
  };

  if (!surveys || surveys.length === 0) {
    return (
      <div className="SurveySelectionPage" data-testid="survey-selection-empty">
        <Typography desktop="h2">{t("surveySelection.noSurveys")}</Typography>
      </div>
    );
  }

  // If we get here and there's only one survey, it means we're waiting for auto-selection
  if (surveys.length === 1) {
    return null;
  }

  return (
    <div className="SurveySelectionPage" data-testid="survey-selection-screen">
      <Typography desktop="h2" className="SurveySelectionPage__title">
        {t("surveySelection.title")}
      </Typography>
      <Typography desktop="body1" className="SurveySelectionPage__subtitle">
        {t("surveySelection.subtitle")}
      </Typography>

      <div className="SurveySelectionPage__surveys">
        {surveys.map((survey) => (
          <Paper
            key={survey.id}
            className="SurveySelectionPage__surveyCard"
            data-testid={`survey-card-${survey.id}`}
          >
            <Typography
              desktop="h3"
              className="SurveySelectionPage__surveyTitle"
            >
              {survey.title}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSelectSurvey(survey)}
              data-testid={`select-survey-${survey.id}`}
            >
              {t("surveySelection.select")}
            </Button>
          </Paper>
        ))}
      </div>
      <Snackbar
        open={launchSurveyMutation.isError}
        onClose={() => launchSurveyMutation.reset()}
        message={t("dispatches.createError")}
      />
    </div>
  );
};
